import { memo, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { cn } from '@/lib/cn'
import { goldGradientButtonSm } from '@/lib/brandGradient'
import { scoreToPercent } from '@/lib/ideaScore'
import { ideaTimeLeftLabel, isTimeLeftUrgent } from '@/lib/ideaTimeLabel'
import { fetchHome, type IdeaListItem } from '@/services/ideasService'
import { IdeaCard, type IdeaCardData } from '@/components/idea/IdeaCard'
import { IdeaCardGridSkeleton } from '@/components/idea/IdeaCardGridSkeleton'
import { useCardVoteHandlers } from '@/hooks/useCardVoteHandlers'
import { useLoaderPhase } from '@/hooks/useLoaderPhase'
import { useAuthGate } from '@/hooks/useAuthGate'
import { AuthPromptModal } from '@/components/AuthPromptModal'

const ChevronRight = ({ className }: { className?: string }) => (
  <svg
    aria-hidden="true"
    className={cn('h-4 w-4', className)}
    viewBox="0 0 24 24"
    fill="none"
  >
    <path
      d="M10 7l5 5-5 5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

function apiItemToIdeaCard(item: IdeaListItem): IdeaCardData {
  // Net score (upvotes - downvotes) so the card pill matches every other
  // surface. `number_of_downvotes` is optional for backward-compat.
  const downs = item.number_of_downvotes ?? 0
  const timeInput = {
    submitted_at: item.submitted_at,
    status: item.status,
    review_opened_at: item.review_opened_at,
    review_closed_at: item.review_closed_at,
    review_duration_days: item.review_duration_days,
  }
  return {
    id: item.id,
    category: item.industry,
    score: scoreToPercent(item.overall_score),
    submittedAt: item.submitted_at,
    title: item.title,
    description: item.one_line_pitch ?? '',
    author: item.submitted_by?.full_name ?? 'Anonymous',
    votes: item.number_of_upvotes - downs,
    hasUpvoted: item.has_upvoted ?? false,
    hasDownvoted: item.has_downvoted ?? false,
    timeLeft: ideaTimeLeftLabel(timeInput),
    timeUrgent: isTimeLeftUrgent(timeInput),
    status: item.status,
    reviewOpenedAt: item.review_opened_at ?? null,
    breakdown: item.score_breakdown ?? null,
    badge: item.badge ?? null,
    // Live counts from the home endpoint (top-level comments / distinct
    // viewers). Undefined for older responses so the card keeps its fallback.
    commentCount: item.feedback_count ?? undefined,
    viewCount: item.view_count ?? undefined,
  }
}

export const LiveIdeasSection = memo(() => {
  const navigate = useNavigate()
  const [ideas, setIdeas] = useState<IdeaCardData[]>([])
  const [loading, setLoading] = useState(true)
  const loaderPhase = useLoaderPhase(loading && ideas.length === 0)
  const { handleUpvote, handleDownvote } = useCardVoteHandlers(ideas, setIdeas)

  // Signed-out visitors can see ideas but can't vote. The gate intercepts the
  // click before the optimistic update, so there's no flicker and no request.
  const { guard, promptProps } = useAuthGate()
  const guardedUpvote = guard('vote-up', handleUpvote)
  const guardedDownvote = guard('vote-down', handleDownvote)

  useEffect(() => {
    let cancelled = false
    fetchHome()
      .then((homeData) => {
        if (!cancelled) {
          setIdeas(
            (homeData.top_ideas ?? []).slice(0, 6).map(apiItemToIdeaCard),
          )
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (loaderPhase !== 'done' && ideas.length === 0) {
    // Within the delay window render nothing (the section simply appears once
    // content is ready); past the threshold crossfade a skeleton grid in.
    if (loaderPhase === 'pending') return null
    return (
      <section className="w-full bg-transparent px-4 pt-10 pb-6 sm:px-6 sm:pt-12 sm:pb-8 lg:pt-14 lg:pb-8">
        <div className="mx-auto w-full max-w-[1200px]">
          <IdeaCardGridSkeleton />
        </div>
      </section>
    )
  }

  if (ideas.length === 0) return null

  return (
    <section className="w-full bg-transparent px-4 pt-10 pb-6 sm:px-6 sm:pt-12 sm:pb-8 lg:pt-14 lg:pb-8">
      <div className="mx-auto w-full max-w-[1200px]">
        <header>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
            <div className="min-w-0">
              <h2 className="text-[28px] leading-tight font-medium tracking-tight text-white">
                Ideas Awaiting Your Insights
              </h2>
              <p className="mt-2 max-w-3xl text-[14px] leading-relaxed font-medium text-white/60">
                These founders need fresh perspectives. Review, validate and
                help shape what gets built next.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/validate-ideas')}
              className={cn(
                'inline-flex shrink-0 items-center gap-1 self-end px-3.5 py-1.5 text-[13px] font-semibold sm:gap-1.5 sm:self-auto sm:px-4 sm:py-2 sm:text-sm',
                goldGradientButtonSm,
              )}
            >
              View all
              <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4" />
            </button>
          </div>
        </header>

        <div className="mt-5 grid min-w-0 grid-cols-1 justify-items-center gap-5 sm:mt-6 sm:gap-6 md:grid-cols-2 xl:grid-cols-3 [&>*]:overflow-visible">
          {ideas.map((idea, index) => (
            <IdeaCard
              key={idea.id}
              idea={idea}
              staggerIndex={index}
              onValidate={(id) => navigate(`/validate-ideas/${id}`)}
              onIndustryClick={(industry) =>
                navigate(
                  `/validate-ideas?industry=${encodeURIComponent(industry)}`,
                )
              }
              onToggleUpvote={guardedUpvote}
              onToggleDownvote={guardedDownvote}
            />
          ))}
        </div>
      </div>
      <AuthPromptModal {...promptProps} />
    </section>
  )
})

LiveIdeasSection.displayName = 'LiveIdeasSection'
